import React, { useCallback, useState } from 'react';
import { getUploadUrl, uploadToS3, processVideoWs } from './api';
import type { VideoInfo } from './types';

interface UploadZoneProps {
  onUploaded: (info: VideoInfo) => void;
}

export function UploadZone({ onUploaded }: UploadZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState('');

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files.length === 0) return;

      await uploadFile(files[0]);
    },
    [onUploaded]
  );

  const handleFileInput = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;

      await uploadFile(files[0]);
    },
    [onUploaded]
  );

  const uploadFile = async (file: File) => {
    setIsUploading(true);
    setProgress('Получение ссылки для загрузки...');

    try {
      // Step 1: Get upload URL
      const { uploadUrl, s3Url } = await getUploadUrl(file.name, file.type);

      // Step 2: Upload to S3
      setProgress('Загрузка видео...');
      await uploadToS3(uploadUrl, file);

      // Step 3: Process video via WebSocket (no timeout issue)
      setProgress('Обработка видео...');
      const videoInfo = await processVideoWs(s3Url, file.name, 27.0, setProgress);

      onUploaded(videoInfo);
    } catch (error) {
      alert('Ошибка загрузки: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsUploading(false);
      setProgress('');
    }
  };

  return (
    <label
      htmlFor="video-upload"
      className={`at-upload-zone ${isDragging ? 'at-dragging' : ''} ${isUploading ? 'at-uploading' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        type="file"
        id="video-upload"
        accept="video/*"
        onChange={handleFileInput}
        disabled={isUploading}
        style={{ display: 'none' }}
      />
        {isUploading ? (
          <div className="at-upload-progress">
            <div className="at-spinner"></div>
            <span>{progress}</span>
          </div>
        ) : (
          <>
            <div className="at-upload-icon">📁</div>
            <div className="at-upload-text">
              <strong>Перетащите видео сюда</strong>
              <span>или нажмите для выбора файла</span>
            </div>
            <div className="at-upload-hint">
              Поддерживаемые форматы: MP4, MOV, AVI
            </div>
          </>
        )}
    </label>
  );
}
